import { createContext, useState, useEffect, useRef, useCallback } from 'react';
import * as api from '@/services/api';

export const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [clients, setClients] = useState([]);
  const [activeClientId, setActiveClientId] = useState(1);
  const [keywords, setKeywords] = useState({});
  const [rankings, setRankings] = useState({});
  const [rankingHistory, setRankingHistory] = useState({});
  const [discoveredKeywords, setDiscoveredKeywords] = useState({});
  const [scanStatus, setScanStatus] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [activeCityFilter, setActiveCityFilter] = useState('all');
  const [activeView, setActiveView] = useState('dashboard');
  const [toasts, setToasts] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  const pollRef = useRef(null);

  const activeClient = clients.find(c => c.id === activeClientId) || null;

  // Toast helpers
  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);

  const removeToast = useCallback((id) => setToasts(prev => prev.filter(t => t.id !== id)), []);

  // Data loaders
  const loadClients = useCallback(async () => {
    const { data, error } = await api.getClients();
    if (error) { addToast(error, 'error'); return; }
    setClients(data || []);
  }, [addToast]);

  const loadKeywords = useCallback(async (clientId) => {
    const { data, error } = await api.getKeywords(clientId);
    if (error) { addToast(error, 'error'); return; }
    setKeywords(prev => ({ ...prev, [clientId]: data || [] }));
  }, [addToast]);

  const loadRankings = useCallback(async (clientId) => {
    const [rankRes, histRes] = await Promise.all([
      api.getRankings(clientId),
      api.getRankingHistory(clientId),
    ]);
    if (rankRes.data) setRankings(prev => ({ ...prev, [clientId]: rankRes.data }));
    if (histRes.data) setRankingHistory(prev => ({ ...prev, [clientId]: histRes.data }));
  }, []);

  const loadDiscovered = useCallback(async (clientId) => {
    const { data } = await api.getDiscovered(clientId);
    if (data) setDiscoveredKeywords(prev => ({ ...prev, [clientId]: data }));
  }, []);

  // Client switcher
  const switchClient = useCallback(async (id) => {
    setActiveClientId(id);
    setActiveCityFilter('all');
    if (!keywords[id]) await loadKeywords(id);
    if (!rankings[id]) await loadRankings(id);
    if (!discoveredKeywords[id]) await loadDiscovered(id);
  }, [keywords, rankings, discoveredKeywords, loadKeywords, loadRankings, loadDiscovered]);

  // Scan
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const startScan = useCallback(async (clientId) => {
    setIsScanning(true);
    const { data, error } = await api.triggerScan(clientId);
    if (error) { addToast('Scan failed: ' + error, 'error'); setIsScanning(false); return; }
    const { jobId } = data;

    pollRef.current = setInterval(async () => {
      const { data: status } = await api.getScanStatus(jobId);
      if (!status) return;
      setScanStatus(status);
      if (status.status === 'completed' || status.status === 'failed') {
        stopPolling();
        setIsScanning(false);
        setScanStatus(null);
        await loadRankings(clientId);
        addToast(status.status === 'completed' ? 'Scan complete!' : 'Scan failed', status.status === 'completed' ? 'success' : 'error');
      }
    }, 2000);
  }, [addToast, stopPolling, loadRankings]);

  // Boot
  useEffect(() => {
    (async () => {
      const { data: clientList } = await api.getClients();
      if (!clientList || clientList.length === 0) { setInitialLoading(false); return; }
      setClients(clientList);
      const firstId = clientList[0].id;
      setActiveClientId(firstId);
      await Promise.all([
        loadKeywords(firstId),
        loadRankings(firstId),
        loadDiscovered(firstId),
      ]);
      setInitialLoading(false);
    })();
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    clients, activeClientId, activeClient, keywords, rankings, rankingHistory,
    discoveredKeywords, scanStatus, isScanning, activeCityFilter, activeView,
    toasts, initialLoading,
    setActiveCityFilter, setActiveView,
    switchClient, startScan, stopPolling,
    loadClients, loadKeywords, loadRankings, loadDiscovered,
    addToast, removeToast,
    setClients, setKeywords, setRankings, setDiscoveredKeywords,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
